import { Inject, Injectable } from '@angular/core';
import { concat, Observable, Observer, of, Subject } from 'rxjs';
import { catchError, concatMap, filter, map, takeUntil, toArray } from 'rxjs/operators';
import { WebSocketServiceBase, WebSocketConnectionState, WebSocketServiceConfig, WS_SERVICE_FACTORY, WSServiceFactory } from 'types';
import { HWServiceConfig } from './hw-service-config';
import { CameraInfo, CameraProfileItem, CameraServiceState, CameraType, SizeInfo } from './hw-service-model';

export type CameraStreamingResponse = 'begin' | 'beginHDR' | 'endHDR';
export type CameraPhotoMode = 'simple' | 'smart' | 'hdr';

export abstract class CameraServiceBase {
  abstract endOfStream$: Observable<any>;
  abstract cameraState$: Observable<CameraServiceState>;
  abstract cameraId$: number;

  abstract connect(): void;

  abstract getCameraList(): Observable<CameraInfo[]>;

  abstract getProfile(profile: string): Observable<CameraProfileItem[]>;

  abstract getFrameSizeList(): Observable<SizeInfo[]>;

  abstract startLocalPreviewForPhoto(x: SizeInfo, mode: CameraPhotoMode): Observable<void>;

  abstract startLocalPreviewForSignature(x: SizeInfo): Observable<void>;

  abstract startStreamingForTakePhoto(x: SizeInfo, mode: CameraPhotoMode, portraitSessionId: string): Observable<CameraStreamingResponse>;

  abstract startStreamingForTakePhotoGlue(x: SizeInfo): Observable<CameraStreamingResponse>;

  abstract startMRZscan(x: SizeInfo, atrMRZup: boolean): Observable<void | any>;

  abstract lights(x: boolean): void;

  abstract stopPreview(): Observable<void>;

  abstract enroll(portraitSessionId: string, pop: boolean, popFormat: string): Observable<EnrollResult | null>;

  abstract enrollGlue(): Observable<EnrollResult | null>;

  abstract scanQr(x: SizeInfo): Observable<void | Uint8Array>;

  abstract disconnect(): void;
}

enum ResponseType {
  None,
  CameraList,
  GetProfile,
  FrameSizeList,
  PreviewStart,
  PreviewStop,
  EnrollResult,
  CapturedPhoto,
  StreamingForPhoto,
}

@Injectable()
export class CameraService extends CameraServiceBase {
  private cameraId: number = 0;
  private cameraState: CameraServiceState = CameraServiceState.Closed;
  private cameraStateSubject: Subject<CameraServiceState>;
  private cameraListObserver: Observer<CameraInfo[]> | null = null;
  private getProfileObserver: Observer<CameraProfileItem[]> | null = null;
  private frameSizeListObserver: Observer<SizeInfo[]> | null = null;
  private captureResultObserver: Observer<EnrollResult | null> | null = null;
  private captureQrResultObserver: Observer<Uint8Array> | null = null;
  private startPreviewSignal: Observer<void> | null = null;
  private stopPreviewSignal: Observer<void> | null = null;
  private isPhotoRunning: Observer<CameraStreamingResponse> | null = null;
  private isPhotoHdr = false;
  private capturedPhotoExpected = false;
  private responseType: ResponseType = ResponseType.None;
  private socket: WebSocketServiceBase;
  private MRZdata: Observer<any> | null = null;
  private endOfStream = new Subject<void>();

  public get endOfStream$(): Observable<any> {
    return this.endOfStream.pipe(takeUntil(this.socket.connectionState$.pipe(filter((x) => x === WebSocketConnectionState.Closed))));
  }

  public get cameraState$(): Observable<CameraServiceState> {
    return this.cameraStateSubject.pipe();
  }

  public get cameraId$(): number {
    return this.cameraId;
  }

  public set cameraId$(id: number) {
    this.cameraId = id;
  }

  public constructor(config: HWServiceConfig, @Inject(WS_SERVICE_FACTORY) createWebSocket: WSServiceFactory) {
    super();
    this.cameraStateSubject = new Subject<CameraServiceState>();
    this.socket = createWebSocket(new WebSocketServiceConfig(config.getHWServiceUrl(), config.getCameraServiceProtocol()));
    this.socket.messages$.subscribe(
      (message) => {
        this.onSocketMessageReceived(message);
      },
      (error) => {
        this.onSocketMessageReceived({ result: false, error: error });
      },
    );
    this.socket.connectionState$.subscribe(
      (state) => {
        this.onSocketConnectionStateChanged(state);
      },
      (error) => {
        this.cameraStateSubject.error(error);
      },
    );
  }

  public connect(): void {
    if (this.canConnect()) {
      this.socket.connect();
    } else {
      this.notifyOperationDenied('CONNECT');
    }
  }

  public getCameraList(): Observable<CameraInfo[]> {
    let cameraListLoader$ = new Observable<CameraInfo[]>((observer) => {
      if (this.canPerformOperation()) {
        this.cameraListObserver = observer;
        this.socket.send({ command: 'list-cameras' });
        this.responseType = ResponseType.CameraList;
      } else {
        this.notifyOperationDenied('GET_CAMERA_LIST', observer);
      }
    });
    return cameraListLoader$.pipe(
      concatMap((cameras) => {
        const frameListLoaders$ = cameras.map((camera) => {
          return this.getFrameSizeListInternal(camera.id).pipe(
            catchError((e) => of<SizeInfo[]>()),
            map((frames) => {
              camera.frames = frames;
              return camera;
            }),
          );
        });
        return concat(...frameListLoaders$).pipe(
          filter((camera) => camera.frames.length > 0),
          toArray(),
        );
      }),
    );
  }

  getProfile(profile: string): Observable<CameraProfileItem[]> {
    return new Observable((observer) => {
      if (this.canPerformOperation()) {
        this.socket.send({ 'command': 'get-camera-profile', 'camera-id': this.cameraId, 'profile': profile });
        this.responseType = ResponseType.GetProfile;
        this.getProfileObserver = observer;
      } else {
        this.notifyOperationDenied('GET_PROFILE');
      }
    });
  }

  public getFrameSizeList(): Observable<SizeInfo[]> {
    return this.getFrameSizeListInternal(this.cameraId);
  }

  private getFrameSizeListInternal(cameraId: number): Observable<SizeInfo[]> {
    return new Observable((observer) => {
      if (this.canPerformOperation()) {
        this.frameSizeListObserver = observer;
        this.socket.send({ 'command': 'list-formats', 'camera-id': cameraId });
        this.responseType = ResponseType.FrameSizeList;
      } else {
        this.notifyOperationDenied('GET_FRAME_SIZE_LIST', observer);
      }
    });
  }

  public startLocalPreviewForPhoto(x: SizeInfo, mode: CameraPhotoMode): Observable<void> {
    return new Observable((observer) => {
      if (this.canStartPreview()) {
        this.socket.send({
          'command': 'start-streaming',
          'camera-id': this.cameraId,
          'width': x.width,
          'height': x.height,
          'view-x': 90,
          'view-y': 0,
          'view-width': 1100,
          'view-height': 800,
          'frame-format': x.format,
          'adjust-parameters': mode !== 'simple',
          'process': 'none',
          'ctrl-profile': mode === 'simple' ? 'photo' : 'hdr-preview',
          'render': 'overlay',
          'watermark':
            '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',
          'watermark-x': 90,
        });
        this.responseType = ResponseType.PreviewStart;
        this.startPreviewSignal = observer;
      } else {
        this.notifyOperationDenied('START_PREVIEW');
      }
    });
  }

  public startLocalPreviewForSignature(x: SizeInfo): Observable<void> {
    return new Observable((observer) => {
      if (this.canStartPreview()) {
        this.socket.send({
          'command': 'start-streaming',
          'camera-id': this.cameraId,
          'width': x.width,
          'height': x.height,
          'view-x': 90,
          'view-y': 0,
          'view-width': 1100,
          'view-height': 800,
          'frame-format': x.format,
          'process': 'signature',
          'ctrl-profile': 'photo',
          'render': 'overlay',
        });
        this.responseType = ResponseType.PreviewStart;
        this.startPreviewSignal = observer;
      } else {
        this.notifyOperationDenied('START_PREVIEW');
      }
    });
  }

  public startStreamingForTakePhoto(x: SizeInfo, mode: CameraPhotoMode, portraitSessionId: string): Observable<CameraStreamingResponse> {
    return new Observable((observer) => {
      if (this.canStartPreview()) {
        this.socket.send({
          'command': 'start-streaming',
          'camera-id': this.cameraId,
          'width': x.width,
          'height': x.height,
          'frame-format': x.format,
          'process': 'none',
          'ctrl-profile': mode === 'simple' ? 'photo' : 'hdr',
          'render': mode === 'hdr' ? 'HDR' : 'none',
          'image-ws': portraitSessionId,
        });
        this.responseType = ResponseType.StreamingForPhoto;
        this.isPhotoRunning = observer;
        this.isPhotoHdr = mode === 'hdr';
      } else {
        this.notifyOperationDenied('START_PHOTO_CAPTURE_STREAMING');
      }
    });
  }

  public startStreamingForTakePhotoGlue(x: SizeInfo): Observable<CameraStreamingResponse> {
    return new Observable((observer) => {
      if (this.canStartPreview()) {
        this.socket.send({
          'command': 'start-streaming',
          'camera-id': this.cameraId,
          'width': x.width,
          'height': x.height,
          'frame-format': x.format,
          'process': 'none',
          'ctrl-profile': 'mrz',
          'render': 'X11',
          'view-x': 80,
          'view-y': 70,
          'view-width': 440,
          'view-height': 330,
        });
        this.responseType = ResponseType.StreamingForPhoto;
        this.isPhotoRunning = observer;
        this.isPhotoHdr = false;
      } else {
        this.notifyOperationDenied('START_PHOTO_CAPTURE_STREAMING');
      }
    });
  }

  public startMRZscan(x: SizeInfo, atrMRZup: boolean): Observable<void | any> {
    return new Observable((observer) => {
      if (this.canStartPreview()) {
        this.startPreviewSignal = observer;
        this.MRZdata = observer;
        this.socket.send({
          'command': 'start-streaming',
          'render': atrMRZup ? 'none' : 'overlay',
          'camera-id': this.cameraId,
          'width': x.width,
          'height': x.height,
          'view-x': 90,
          'view-y': 0,
          'view-width': 1100,
          'view-height': 800,
          'frame-format': x.format,
          'process': 'MRZ',
          'upper': atrMRZup,
          'ctrl-profile': 'mrz',
          'watermark':
            'iVBORw0KGgoAAAANSUhEUgAABEwAAAMgAQMAAAADXmVWAAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAAAGUExURUdwTFm70QqiBF8AAAABdFJOUwBA5thmAAAAjElEQVQYGe3AAQ0AMAgDsPlXiwMu40toAwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFBlW0wAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA4IxtMQEAAAAAAAAAgI8eeTES3vnCm6kAAAAASUVORK5CYII=',
          'watermark-x': 90,
        });
        this.responseType = ResponseType.PreviewStart;
      } else {
        this.notifyOperationDenied('SCAN_MRZ', observer);
      }
    });
  }

  public lights(x: boolean): void {
    this.socket.send({
      command: 'light',
      light: x,
    });
  }

  public stopPreview(): Observable<void> {
    return new Observable((observer) => {
      if (this.canStopPreview()) {
        this.socket.send({ command: 'stop-streaming' });
        this.responseType = ResponseType.PreviewStop;
        this.stopPreviewSignal = observer;
      } else {
        this.notifyOperationDenied('STOP_PREVIEW');
      }
    });
  }

  // Если pop=true, то в результате выполнения высылается блоб, затем вызывается complete.
  // Если pop=false, то сразу вызывается complete.
  public enroll(portraitSessionId: string, pop: boolean, popFormat: string): Observable<EnrollResult | null> {
    return new Observable((observer) => {
      if (this.canPerformOperation()) {
        this.captureResultObserver = observer;
        this.socket.send({
          'command': 'capture',
          'pop': pop,
          'delay': 0,
          'stop': true,
          'pop-format': popFormat,
          'image-ws': portraitSessionId,
        });
        this.responseType = ResponseType.EnrollResult;
        this.capturedPhotoExpected = pop;
      } else {
        this.notifyOperationDenied('START_PREVIEW');
      }
    });
  }

  public enrollGlue(): Observable<EnrollResult | null> {
    return new Observable((observer) => {
      if (this.canPerformOperation()) {
        this.captureResultObserver = observer;
        this.socket.send({
          command: 'capture-glue',
        });
        this.responseType = ResponseType.EnrollResult;
        this.capturedPhotoExpected = true;
      } else {
        this.notifyOperationDenied('START_PREVIEW');
      }
    });
  }

  public scanQr(x: SizeInfo): Observable<void | Uint8Array> {
    return new Observable((observer) => {
      if (this.canStartPreview()) {
        this.startPreviewSignal = observer;
        this.captureQrResultObserver = observer;
        this.socket.send({
          'process': 'barcode',
          'post-process': 'decode-b64-unbzip',
          'command': 'start-streaming',
          'render': 'overlay',
          'camera-id': this.cameraId,
          'width': x.width,
          'height': x.height,
          'view-x': 90,
          'view-y': 0,
          'view-width': 1100,
          'view-height': 800,
          'frame-format': x.format,
          'ctrl-profile': 'qr',
          'watermark':
            'iVBORw0KGgoAAAANSUhEUgAABEwAAAMgAQMAAAADXmVWAAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAAAGUExURUdwTFm70QqiBF8AAAABdFJOUwBA5thmAAACFUlEQVR42u3dsQ2CQBSA4Rc1sXMFHcEBTHQ0R2MkJgAbbQTDcQEE+P5GAxRf7lFeuAhJkiRJkiRJkiRJkiRJkiRJkiRJkiRp5A6fP5f2+4/37w4FBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBSWzOrdyzZQid0BXFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFJSlUyKREigoKCgoKCgoW6OcGxuXjolbnKrm9qjnwKvSgzL2gOZESXxXTigoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKNugdISCgoKCgoIyIKX7w1H39htFjP7Nqcaq/KRMPyCUNsqt9bE9CgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKyqIpTin4K2VRh2t05CAWFBQUFBQUJ4aioKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKBMRqnTqlZMOTe2TPWgfF96DrwqkfiuxPgDQkFBQUFBQUGZO+WQSNmhoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoCyeUudWrpkSuQMKFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUSZIkSZIkSZIkSZIkSZIkSZIkSZIkKasX/3pPpdADncUAAAAASUVORK5CYII=',
          'watermark-x': 90,
        });
        this.responseType = ResponseType.PreviewStart;
      } else {
        this.notifyOperationDenied('SCAN_QR', observer);
      }
    });
  }

  public disconnect(): void {
    if (this.canDisconnect()) {
      this.resetResponseSettings();
      this.socket.disconnect();
    } else {
      this.notifyOperationDenied('DISCONNECT');
    }
  }

  private canPerformOperation(): boolean {
    return this.cameraState !== CameraServiceState.Closed && this.responseType === ResponseType.None;
  }

  private canStartPreview(): boolean {
    return this.cameraState === CameraServiceState.Ready && this.responseType === ResponseType.None;
  }

  private canStopPreview(): boolean {
    return this.cameraState === CameraServiceState.Ready && this.responseType === ResponseType.None;
  }

  private canConnect(): boolean {
    return this.cameraState === CameraServiceState.Closed;
  }

  private canDisconnect(): boolean {
    return this.cameraState !== CameraServiceState.Closed;
  }

  private setCameraState(state: CameraServiceState): void {
    if (this.cameraState !== state) {
      this.cameraState = state;
      this.cameraStateSubject.next(state);
    }
  }

  private onSocketConnectionStateChanged(state: WebSocketConnectionState): void {
    switch (state) {
      case WebSocketConnectionState.Opened:
        this.setCameraState(CameraServiceState.Ready);
        break;
      case WebSocketConnectionState.Connecting:
      case WebSocketConnectionState.Disconnecting:
      case WebSocketConnectionState.Closed:
        this.setCameraState(CameraServiceState.Closed);
        break;
    }
  }

  private onSocketMessageReceived(message: any): void {
    switch (message.command) {
      case 'light':
      case 'streaming':
        return;
    }
    switch (this.responseType) {
      case ResponseType.CameraList:
        this.onCameraListReceived(message);
        break;
      case ResponseType.GetProfile:
        this.onGetProfileReceived(message);
        break;
      case ResponseType.FrameSizeList:
        this.onFrameSizeListReceived(message);
        break;
      case ResponseType.PreviewStart:
        this.onPreviewStartResponseReceived(message);
        break;
      case ResponseType.PreviewStop:
        this.onPreviewStopResponseReceived(message);
        break;
      case ResponseType.StreamingForPhoto:
        this.onStreamingForPhoto(message);
        break;
      case ResponseType.EnrollResult:
        this.onEnrollResultResponseReceived(message);
        break;
      case ResponseType.CapturedPhoto:
        this.onCapturedPhotoResponseReceived(message);
        break;
      case ResponseType.None: {
        if (message.error === 'barcode scanned but could not be decoded') {
          this.onQrScannedBadResponseReceived();
          break;
        }
        if (!!message.data) {
          this.onQrScannedGoodResponseReceived(message);
          break;
        }
        if (!!message.lower) {
          this.onMRZScannedResponseReceived(message);
          break;
        }
        if (message.command === 'end-of-stream') {
          this.endOfStream.next();
        }
        break;
      }
    }
  }

  private onCameraListReceived(message: any): void {
    if (!this.cameraListObserver) {
      return;
    }

    const observer = this.cameraListObserver;
    this.cameraListObserver = null;
    this.responseType = ResponseType.None;

    if (Array.isArray(message.cameras)) {
      const list: CameraInfo[] = [];
      for (let i = 0; i < message.cameras.length; ++i) {
        let id: number | null = null;
        let type: CameraType | null = null;
        if (typeof message.cameras[i].id === 'number') {
          id = message.cameras[i].id;
        }
        if (typeof message.cameras[i].type == 'string') {
          if (message.cameras[i].type === 'back') {
            type = CameraType.Back;
          }
          if (message.cameras[i].type === 'front') {
            type = CameraType.Front;
          }
          if (message.cameras[i].type === 'primary') {
            type = CameraType.Primary;
          }
        }
        if (id !== null && type !== null) {
          list.push(new CameraInfo(id, type, []));
        }
      }
      observer.next(list);
      observer.complete();
    } else {
      observer.error('Invalid camera list format');
    }
  }

  private onGetProfileReceived(message: any) {
    if (!this.getProfileObserver) {
      return;
    }

    const observer = this.getProfileObserver;
    this.getProfileObserver = null;
    this.responseType = ResponseType.None;

    if (Array.isArray(message['controls'])) {
      const list: CameraProfileItem[] = [];
      for (const src of message['controls']) {
        if (src.name === undefined || src.value === undefined) {
          continue;
        }
        const item = new CameraProfileItem();
        item.name = src.name;
        item.value = src.value;
        list.push(item);
      }
      observer.next(list);
      observer.complete();
    } else {
      observer.error(message.error);
    }
  }

  private onFrameSizeListReceived(message: any): void {
    if (!this.frameSizeListObserver) {
      return;
    }

    const observer = this.frameSizeListObserver;
    this.frameSizeListObserver = null;
    this.responseType = ResponseType.None;

    if (Array.isArray(message['formats'])) {
      const list: SizeInfo[] = [];
      const sizes = message['formats'];

      for (const size of sizes) {
        const width = typeof size.width === 'number' ? size.width : null;
        const height = typeof size.height === 'number' ? size.height : null;
        const format = typeof size['frame-format'] === 'string' ? size['frame-format'] : null;

        if (width !== null && height !== null) {
          list.push(new SizeInfo(width, height, format));
        }
      }

      observer.next(list);
      observer.complete();
    } else {
      observer.error('Invalid frame sizes list format');
    }
  }

  private onPreviewStartResponseReceived(message: any): void {
    if (!this.startPreviewSignal) {
      return;
    }

    const obs = this.startPreviewSignal;
    this.startPreviewSignal = null;

    this.responseType = ResponseType.None;

    if (message.result) {
      obs.next();
      if (this.MRZdata === null && this.captureQrResultObserver === null) {
        // complete only if we are not MRZ/QR mode
        obs.complete();
      }
    } else {
      obs.error(message.error);
      this.cameraStateSubject.error(message.error);
    }
  }

  private onStreamingForPhoto(message: any): void {
    if (!this.isPhotoRunning) {
      return;
    }
    if (message.result) {
      if (message.command === 'start-streaming') {
        const observer = this.isPhotoRunning;

        if (!this.isPhotoHdr) {
          this.isPhotoRunning = null;
          this.responseType = ResponseType.None;
        }

        if (!this.isPhotoHdr) {
          observer.next('begin');
          observer.complete();
        } else {
          observer.next('beginHDR');
        }
      } else if (message.command === 'HDR') {
        const observer = this.isPhotoRunning;

        this.isPhotoRunning = null;
        this.responseType = ResponseType.None;

        observer.next('endHDR');
        observer.complete();
      }
    } else {
      const observer = this.isPhotoRunning;

      this.isPhotoRunning = null;
      this.responseType = ResponseType.None;

      observer.error(message.error);
      this.cameraStateSubject.error(message.error);
    }
  }

  private onPreviewStopResponseReceived(message: any): void {
    if (!this.stopPreviewSignal) {
      return;
    }

    const obs = this.stopPreviewSignal;
    this.stopPreviewSignal = null;

    this.responseType = ResponseType.None;

    if (message.result) {
      obs.next();
      obs.complete();
    } else {
      obs.error(message.error);
      this.cameraStateSubject.error(message.error);
    }
  }

  private onEnrollResultResponseReceived(message: any): void {
    if (this.capturedPhotoExpected) {
      this.onCapturedPhotoResponseReceived(message);
    } else {
      this.onPhotoEnqueueResponseReceived(message);
    }
  }

  private onCapturedPhotoResponseReceived(message: any): void {
    if (!this.captureResultObserver) {
      return;
    }
    if (message.result && message.command === 'capture-glue' && message.data === undefined) {
      return;
    }
    const observer = this.captureResultObserver;
    this.captureResultObserver = null;
    this.capturedPhotoExpected = false;
    this.responseType = ResponseType.None;
    if (message.result) {
      observer.next(message);
      observer.complete();
    } else {
      observer.error(message.error);
    }
  }

  private onPhotoEnqueueResponseReceived(message: any): void {
    if (!this.captureResultObserver) {
      return;
    }
    const observer = this.captureResultObserver;
    this.captureResultObserver = null;
    this.capturedPhotoExpected = false;
    this.responseType = ResponseType.None;
    if (message.result) {
      observer.next(null);
      observer.complete();
    } else {
      observer.error(message.error);
    }
  }

  private onQrScannedBadResponseReceived(): void {
    if (!this.captureQrResultObserver) {
      return;
    }
    this.captureQrResultObserver.error('');
    this.captureQrResultObserver = null;
    this.responseType = ResponseType.None;
  }

  private onQrScannedGoodResponseReceived(message: any): void {
    if (!this.captureQrResultObserver) {
      return;
    }
    this.captureQrResultObserver.next(new TextEncoder().encode(message.data));
    this.captureQrResultObserver.complete();
    this.captureQrResultObserver = null;
    this.responseType = ResponseType.None;
  }

  private onMRZScannedResponseReceived(message: any): void {
    if (!this.MRZdata) {
      return;
    }
    this.MRZdata.next(message);
    this.MRZdata.complete();
    this.MRZdata = null;
    this.responseType = ResponseType.None;
  }

  private notifyOperationDenied<T>(operation: string, observer: Observer<T> | null = null): void {
    const message =
      'Cannot perform operation "' +
      operation +
      '". ' +
      'Current state: ' +
      CameraServiceState[this.cameraState] +
      '. ' +
      'Expected response: ' +
      ResponseType[this.responseType] +
      '.';

    if (observer) {
      observer.error(message);
    } else {
      this.cameraStateSubject.error(message);
    }
  }

  private resetResponseSettings(): void {
    this.responseType = ResponseType.None;
    this.frameSizeListObserver = null;
    this.cameraListObserver = null;
    this.captureResultObserver = null;
    this.capturedPhotoExpected = false;
  }
}

export class EnrollResult {
  result: boolean;
  data: string;
}
